import { useCallback, useEffect, useState } from "react";
import { Lightbulb, Play, CheckCheck, AlertTriangle, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { playVoice } from "@/lib/tts";
import HelpStats from "@/components/admin/HelpStats";

const SUBJECTS = ["Maths", "English", "French", "Science", "History & Geography", "SSEE", "Health & Physical Education"];
const pill = (on) => `px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${on ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600"}`;

function HintRow({ it, onSaved }) {
  const [text, setText] = useState(it.hint);
  const save = async (approved) => {
    if (approved && it.flagged && !window.confirm("This hint may reveal the answer. Approve it anyway?")) return;
    try {
      await api.put(`/admin/kreol-hints/${encodeURIComponent(it.id)}`, { hint: text, approved });
      toast.success(approved ? "Hint approved" : "Hint saved (hidden from children)");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-slate-700 p-3 space-y-2" data-testid={`kreol-row-${it.id}`}>
      <p className="text-xs text-slate-400">{it.chapter_id} · Level {it.level} · Q{it.qi + 1}
        {it.approved && <span className="ml-2 text-green-400 font-semibold">Approved</span>}
        {it.flagged && <span className="ml-2 text-rose-400 font-semibold inline-flex items-center gap-1"><AlertTriangle size={12} /> May reveal the answer</span>}
        {it.stale && <span className="ml-2 text-amber-400 font-semibold">Question changed since the hint was written</span>}
      </p>
      <p className="text-sm font-semibold">{it.question}</p>
      <p className="text-xs text-slate-400">{it.options.join(" · ")} — <span className="text-green-400">answer: {it.answer}</span></p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} data-testid={`kreol-text-${it.id}`}
        className="w-full rounded-lg bg-slate-900 border border-slate-600 p-2 text-sm" />
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => playVoice(text, "fr")} className={pill(false)} data-testid={`kreol-listen-${it.id}`}><Volume2 size={14} className="inline mr-1" />Listen</button>
        <button onClick={() => save(true)} className="px-3 py-1.5 rounded-full bg-green-500 text-slate-900 text-sm font-bold" data-testid={`kreol-approve-${it.id}`}>Save &amp; approve</button>
        <button onClick={() => save(false)} className={pill(false)} data-testid={`kreol-hide-${it.id}`}>Save, keep hidden</button>
      </div>
    </div>
  );
}

export default function KreolHintsAdmin() {
  const [st, setSt] = useState(null);
  const [gen, setGen] = useState([1, 2, 3]);
  const [grade, setGrade] = useState(1);
  const [subject, setSubject] = useState("Maths");
  const [show, setShow] = useState("pending");
  const [skip, setSkip] = useState(0);
  const [list, setList] = useState({ total: 0, items: [] });

  const loadStatus = useCallback(() => api.get("/admin/kreol-hints/status").then(({ data }) => setSt(data)).catch(() => {}), []);
  const loadList = useCallback(() => api.get("/admin/kreol-hints", { params: { grade, subject, show, skip } })
    .then(({ data }) => setList(data)).catch(() => {}), [grade, subject, show, skip]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!st?.job?.running) return undefined;
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [st?.job?.running, loadStatus]);

  const start = async () => {
    try { const { data } = await api.post("/admin/kreol-hints/generate", { grades: gen }); setSt(data); toast.success("Writing hints in the background"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const approveAll = async () => {
    if (!window.confirm(`Approve every pending hint of Grade ${grade} ${subject} (except flagged ones)?`)) return;
    const { data } = await api.post("/admin/kreol-hints/approve", { grade, subject });
    toast.success(`${data.approved} hints approved`);
    loadList(); loadStatus();
  };
  const job = st?.job || {};
  const counts = (st?.counts || []).filter((c) => c.grade === grade);

  return (
    <div className="space-y-6" data-testid="kreol-admin">
      <HelpStats />
      <div className="rounded-2xl border border-slate-700 p-5 space-y-3">
        <h3 className="text-lg font-bold inline-flex items-center gap-2"><Lightbulb size={18} /> Kreol hints ("Ed mwa")</h3>
        <p className="text-sm text-slate-400">AI (Claude Sonnet) writes one short Kreol Morisien hint per question. Children only see approved hints.</p>
        <div className="flex gap-2 flex-wrap items-center">
          {[1, 2, 3, 4, 5, 6].map((g) => (
            <button key={g} onClick={() => setGen((x) => (x.includes(g) ? x.filter((y) => y !== g) : [...x, g]))} className={pill(gen.includes(g))} data-testid={`kreol-gen-grade-${g}`}>Grade {g}</button>
          ))}
          <button onClick={start} disabled={job.running || !gen.length} data-testid="kreol-generate-btn"
            className="px-4 py-1.5 rounded-full bg-blue-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1"><Play size={14} /> Write missing hints</button>
        </div>
        {job.started_at && (
          <p className="text-sm" data-testid="kreol-job-status">
            {job.running ? "Writing…" : "Last run finished"} · Grades {(job.grades || []).join(", ")} · {job.done || 0}/{job.total || 0} chapter levels · {job.made || 0} hints
            {job.errors ? ` · ${job.errors} batches failed (run again to retry)` : ""}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-slate-700 p-5 space-y-3">
        <div className="flex gap-2 flex-wrap">
          {[1, 2, 3, 4, 5, 6].map((g) => <button key={g} onClick={() => { setGrade(g); setSkip(0); }} className={pill(grade === g)} data-testid={`kreol-grade-${g}`}>Grade {g}</button>)}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select value={subject} onChange={(e) => { setSubject(e.target.value); setSkip(0); }} data-testid="kreol-subject"
            className="rounded-lg bg-slate-900 border border-slate-600 px-2 py-1.5 text-sm">
            {SUBJECTS.map((s) => <option key={s}>{s}</option>)}
          </select>
          {["pending", "flagged", "all"].map((v) => <button key={v} onClick={() => { setShow(v); setSkip(0); }} className={pill(show === v)} data-testid={`kreol-show-${v}`}>{v}</button>)}
          <button onClick={approveAll} className="px-3 py-1.5 rounded-full bg-green-500 text-slate-900 text-sm font-bold inline-flex items-center gap-1" data-testid="kreol-approve-all"><CheckCheck size={14} /> Approve all pending (not flagged)</button>
        </div>
        <p className="text-xs text-slate-400" data-testid="kreol-counts">
          {counts.map((c) => `${c.subject}: ${c.approved}/${c.total} approved${c.flagged ? `, ${c.flagged} flagged` : ""}`).join(" · ") || "No hints yet for this grade."}
        </p>
        <div className="space-y-3">
          {list.items.map((it) => <HintRow key={`${it.id}-${it.hint}-${it.approved}`} it={it} onSaved={() => { loadList(); loadStatus(); }} />)}
        </div>
        {list.total > 50 && (
          <div className="flex gap-2 items-center text-sm">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))} className={pill(false)} data-testid="kreol-prev">Previous</button>
            <span data-testid="kreol-page">{skip + 1}–{Math.min(skip + 50, list.total)} of {list.total}</span>
            <button disabled={skip + 50 >= list.total} onClick={() => setSkip(skip + 50)} className={pill(false)} data-testid="kreol-next">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
